import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageCircle, MapPin } from "lucide-react";
import MemberAvatar from "@/components/common/MemberAvatar";
import ProgramBadge from "./ProgramBadge";
import AvailabilityPill, { PresencePill } from "./AvailabilityPill";
import { Button } from "@/components/ui/button";

export default function SponsorCard({ sponsor, onFavorite, onMessage }) {
  const navigate = useNavigate();
  const u = sponsor.user || {};
  const loc = [u.location?.city, u.location?.state].filter(Boolean).join(", ");

  return (
    <article
      data-testid={`sponsor-card-${sponsor.user_id}`}
      className="sm-card sm-card-hover p-5 flex flex-col gap-4 bg-white"
    >
      <div className="flex items-start gap-3">
        <MemberAvatar user={u} size={52} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <Link
              to={`/sponsors/${sponsor.user_id}`}
              data-testid={`sponsor-name-${sponsor.user_id}`}
              className="font-display text-lg font-bold text-[#1F2A2E] hover:text-[#1F6F78] truncate"
            >
              {u.display_name}
            </Link>
            {onFavorite && (
              <button
                onClick={() => onFavorite(sponsor)}
                data-testid={`favorite-button-${sponsor.user_id}`}
                aria-label={sponsor.is_favorite ? "Remove from favorites" : "Add to favorites"}
                className="p-1.5 rounded-full hover:bg-[#F2EFE8] shrink-0"
              >
                <Heart
                  className={`w-5 h-5 ${
                    sponsor.is_favorite ? "fill-[#D07A4A] text-[#D07A4A]" : "text-[#7A868B]"
                  }`}
                />
              </button>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <ProgramBadge program={sponsor.recovery_program} />
            <PresencePill presence={sponsor.presence} />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#4B5A60]">
        {sponsor.years_in_recovery ? (
          <span data-testid={`years-recovery-${sponsor.user_id}`} className="font-semibold text-[#1F2A2E]">
            {sponsor.years_in_recovery} {sponsor.years_in_recovery === 1 ? "year" : "years"} in recovery
          </span>
        ) : null}
        {sponsor.years_sponsoring ? (
          <span>{sponsor.years_sponsoring} yrs sponsoring</span>
        ) : null}
        {loc ? (
          <span className="inline-flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {loc}
          </span>
        ) : null}
      </div>

      <AvailabilityPill status={sponsor.availability_status} />

      {sponsor.about_me ? (
        <blockquote className="text-sm text-[#4B5A60] leading-6 border-l-2 border-[#D7EEF0] pl-3 line-clamp-3">
          {sponsor.about_me}
        </blockquote>
      ) : null}

      <div className="mt-auto flex gap-2 pt-1">
        <Button
          onClick={() => navigate(`/sponsors/${sponsor.user_id}`)}
          data-testid={`view-profile-${sponsor.user_id}`}
          variant="outline"
          className="flex-1 bg-white border-[#1F6F78] text-[#1F6F78] hover:bg-[#D7EEF0]"
        >
          View Profile
        </Button>
        <Button
          onClick={() => onMessage?.(sponsor)}
          data-testid={`message-button-${sponsor.user_id}`}
          className="flex-1 bg-[#1F6F78] hover:bg-[#185B62] text-white"
        >
          <MessageCircle className="w-4 h-4 mr-1.5" /> Message
        </Button>
      </div>
    </article>
  );
}
